import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSyncAlt, FaTimes } from 'react-icons/fa';
import CollapsibleFilters from './CollapsibleFilters';
import { getActiveStoreId } from './entityContext';
import { fetchGroceryOrderHistoryByStore, fetchGroceryOrdersByStore, updateGroceryOrderStatus } from './groceryApi';
import {
  deliveryAgentsToSelectOptions,
  getDeliveryAgentDisplayName,
  getDeliveryAgentPhone,
  getDeliveryAgentRecordId,
} from './deliveryAgentUtils';
import OrderHistoryDateFilters, {
  defaultOrderHistoryFromDate,
  defaultOrderHistoryToDate,
  orderMatchesHistoryDateRange,
} from './OrderHistoryDateFilters';
import OrderStatusFilterDropdown from './OrderStatusFilterDropdown';
import OrdersTablePanel from './OrdersTablePanel';
import SearchableSelect from './SearchableSelect';
import ToolbarRichSelect, { ORDER_VIEW_OPTIONS } from './ToolbarRichSelect';
import { isRestaurantStoreOrderSource } from './menuChannels';
import { normalizeOnlineOrderStatusForFilter } from './orderStatusApi';
import OrderStatusPill from './ui/OrderStatusPill';
import OrderItemsSummary from './ui/OrderItemsSummary';
import {
  getNextStoreOrderStep,
  resolveStatusKey,
  storeOrderStatusChannel,
} from './ui/orderStatusFlow';
import './css/OrderStatusPill.css';
import StoreExpectedDeliveryTimePicker from './StoreExpectedDeliveryTimePicker';
import { fetchStoreDeliveryAgents } from './storeUserApi';
import {
  clampExpectedDeliveryParts,
  expectedDeliveryMinDateValue,
  expectedDeliveryMinTimeValue,
  formatDisplayableExpectedDelivery,
  formatExpectedDeliveryTimeForApi,
  formatStoreOrderDateTime,
  getDisplayableExpectedDeliveryTime,
  isExpectedDeliveryBeforeCreated,
  isOrderCreatedToday,
  joinDateAndTimeLocal,
  toDateInputValue,
  toTimeInputValue,
} from './storeOrderDateUtils';
import './css/Order.css';
import './css/Purchase.css';
import './css/TodayOrder.css';
import { confirmAction } from './ui/feedback';
import { canUpdateOrderStatus } from './rbacSession';

/** Store active orders — status checkbox filters. */
const STORE_ACTIVE_STATUS_OPTS = [
  { code: 'NEW', label: 'NEW', matchNorm: ['NEW', 'PENDING'] },
  { code: 'RDY', label: 'READY', matchNorm: ['READY', 'RDY'] },
  {
    code: 'OFD',
    label: 'OUT-FOR-DELIVERY',
    matchNorm: ['OUTFORDELIVERY', 'OFD', 'INTRANSIT', 'INTRANST'],
  },
];

const STORE_CANON_TO_CODE = {
  NEW: 'NEW',
  ACCEPTED: 'NEW',
  PREPARING: 'NEW',
  READY: 'RDY',
  OFD: 'OFD',
};

function buildSingleStatusSelection(opts, code) {
  return Object.fromEntries(opts.map((o) => [o.code, o.code === code]));
}

/** Store order history — POS (pickup / counter; CMP and delivery DEL). */
const STORE_POS_HISTORY_STATUS_OPTS = [
  { code: 'HIST_CMP', label: 'COMPLETED', matchNorm: ['COMPLETED', 'COMPLETE', 'DELIVERED', 'PAID', 'CMP', 'DEL'] },
  { code: 'HIST_CANC', label: 'CANCELLED', matchNorm: ['CANCELLED', 'CAN'] },
  { code: 'HIST_UNDL', label: 'UNDELIVERED', matchNorm: ['UNDELIVERED', 'UND'] },
];

/** Store order history — My Eats / Restaurant (delivery). */
const STORE_DELIVERY_HISTORY_STATUS_OPTS = [
  { code: 'HIST_DLVD', label: 'DELIVERED', matchNorm: ['DELIVERED', 'DEL'] },
  { code: 'HIST_CANC', label: 'CANCELLED', matchNorm: ['CANCELLED', 'CAN'] },
  { code: 'HIST_UNDL', label: 'UNDELIVERED', matchNorm: ['UNDELIVERED', 'UND'] },
];

function getStoreHistoryStatusOpts(orderTypeFilter) {
  return orderTypeFilter === 'pos'
    ? STORE_POS_HISTORY_STATUS_OPTS
    : STORE_DELIVERY_HISTORY_STATUS_OPTS;
}

function normalizeStoreOrderView(val) {
  if (val === 'history' || val === 'orderHistory' || val === 'delivered') return 'orderHistory';
  return 'orders';
}

function buildEmptyStatusSelections(opts) {
  return Object.fromEntries(opts.map((o) => [o.code, false]));
}

function normalizeStoreOrderStatusNorm(order) {
  const raw =
    order?.orderStatusCode ??
    order?.orderStatus ??
    order?.Order_Status_Code ??
    order?.OrderStatus ??
    '';
  return normalizeOnlineOrderStatusForFilter(raw);
}

function orderPassesStatusFilter(order, selections, opts) {
  const allSel = opts.every((o) => selections[o.code]);
  if (allSel) return true;
  const anySel = opts.some((o) => selections[o.code]);
  if (!anySel) return true;
  const norm = normalizeStoreOrderStatusNorm(order);
  for (const opt of opts) {
    if (opt.matchNorm.includes(norm)) return !!selections[opt.code];
  }
  return false;
}

function formatMoney(val) {
  const n = Number(val);
  if (!Number.isFinite(n)) return '—';
  return `₹${n.toFixed(2)}`;
}

function formatDate(raw) {
  return formatStoreOrderDateTime(raw);
}

function getOrderLineItems(order) {
  const raw = order?.lineItems ?? order?.orderLineItems ?? order?.LineItems ?? [];
  return Array.isArray(raw) ? raw : [];
}

function normalizeStoreOrderSource(order) {
  return String(order?.orderSource ?? order?.Order_Source ?? '')
    .trim()
    .toUpperCase();
}

/** Filter store orders by sidebar channel (POS / My Eats / Restaurant). */
function orderMatchesStoreChannel(order, channel) {
  const source = normalizeStoreOrderSource(order);
  if (channel === 'pos') return source === 'POS' || source === '';
  if (channel === 'myeats') return source === 'MYEATS';
  if (channel === 'restaurant') return isRestaurantStoreOrderSource(source);
  return true;
}

function formatStoreOrderCustomer(order) {
  const name = order?.receiverName;
  const phone = order?.receiverPhone;
  if (name && phone) return `${name} · ${phone}`;
  return name || phone || '—';
}

function formatStoreOrderRestaurant(order) {
  const name = order?.restaurantName ?? order?.Restaurant_Name;
  if (name) return name;
  return order?.receiverName || '—';
}

function isDeliveryStoreOrder(order) {
  const type = String(order?.orderType ?? order?.Order_Type ?? 'DELIVERY')
    .trim()
    .toUpperCase();
  return type === 'DELIVERY' || type === '';
}

function shouldOfferDeliveryAgent(orderTypeFilter) {
  return orderTypeFilter === 'myeats' || orderTypeFilter === 'restaurant';
}

const StoreOrders = ({
  orderTypeFilter = 'pos',
  initialActiveStatus = '',
  serviceChannelSelect = null,
}) => {
  const navigate = useNavigate();
  const storeId = getActiveStoreId();
  const allowOrderStatusUpdate = canUpdateOrderStatus();
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  }, []);
  const updatedById = Number(user?.userId || user?.id || user?.User_Id || 0);

  const isRestaurantChannel = orderTypeFilter === 'restaurant';
  const isPosChannel = orderTypeFilter === 'pos';
  const partyColumnLabel = isRestaurantChannel ? 'Company' : 'Customer';

  const [orderView, setOrderView] = useState('orders');
  const isHistoryView = orderView === 'orderHistory';
  const hidePosActiveStatusFilter = isPosChannel && !isHistoryView;
  const showDeliveryAgentColumn = !isHistoryView && shouldOfferDeliveryAgent(orderTypeFilter);
  const [activeStatusSelections, setActiveStatusSelections] = useState(() =>
    buildEmptyStatusSelections(STORE_ACTIVE_STATUS_OPTS)
  );
  const [historyStatusSelections, setHistoryStatusSelections] = useState(() =>
    buildEmptyStatusSelections(getStoreHistoryStatusOpts(orderTypeFilter))
  );
  const [historyFromDate, setHistoryFromDate] = useState(defaultOrderHistoryFromDate);
  const [historyToDate, setHistoryToDate] = useState(defaultOrderHistoryToDate);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [actionId, setActionId] = useState(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignOrder, setAssignOrder] = useState(null);
  const [deliveryAgents, setDeliveryAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [assignSaving, setAssignSaving] = useState(false);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
  const [expectedDeliveryTime, setExpectedDeliveryTime] = useState('');

  const loadOrders = useCallback(async () => {
    if (!storeId) {
      setOrders([]);
      setError('No store selected. Choose one from the profile menu.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const rows = isHistoryView
        ? await fetchGroceryOrderHistoryByStore(storeId)
        : await fetchGroceryOrdersByStore(storeId);
      setOrders(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setOrders([]);
      setError(e?.response?.data?.message || 'Failed to load store orders.');
    } finally {
      setLoading(false);
    }
  }, [storeId, isHistoryView]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    setActiveStatusSelections(buildEmptyStatusSelections(STORE_ACTIVE_STATUS_OPTS));
    setHistoryStatusSelections(buildEmptyStatusSelections(getStoreHistoryStatusOpts(orderTypeFilter)));
  }, [orderView, orderTypeFilter]);

  useEffect(() => {
    if (!initialActiveStatus || hidePosActiveStatusFilter) return;
    const canon = resolveStatusKey(initialActiveStatus);
    const code = STORE_CANON_TO_CODE[canon];
    if (code) {
      setOrderView('orders');
      setActiveStatusSelections(buildSingleStatusSelection(STORE_ACTIVE_STATUS_OPTS, code));
    }
  }, [initialActiveStatus, orderTypeFilter, hidePosActiveStatusFilter]);

  const statusFilterOpts = isHistoryView
    ? getStoreHistoryStatusOpts(orderTypeFilter)
    : STORE_ACTIVE_STATUS_OPTS;
  const statusSelections = isHistoryView ? historyStatusSelections : activeStatusSelections;

  const toggleStatusFilter = (code) => {
    if (isHistoryView) {
      setHistoryStatusSelections((prev) => ({ ...prev, [code]: !prev[code] }));
    } else {
      setActiveStatusSelections((prev) => ({ ...prev, [code]: !prev[code] }));
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const statusOpts = isHistoryView
      ? getStoreHistoryStatusOpts(orderTypeFilter)
      : STORE_ACTIVE_STATUS_OPTS;
    const selections = isHistoryView ? historyStatusSelections : activeStatusSelections;
    return orders.filter((order) => {
      if (!orderMatchesStoreChannel(order, orderTypeFilter)) return false;
      if (!isHistoryView && !isOrderCreatedToday(order)) return false;
      if (isHistoryView && !orderMatchesHistoryDateRange(order, historyFromDate, historyToDate)) {
        return false;
      }
      if (!hidePosActiveStatusFilter && !orderPassesStatusFilter(order, selections, statusOpts)) return false;
      if (!q) return true;
      const haystack = [
        order.groceryOrderId,
        order.displayOrderRef,
        order.masterOrderId,
        order.receiverName,
        order.receiverPhone,
        order.restaurantName,
        order.deliveryUserName,
        order.deliveryUserPhone,
        order.orderType,
        order.orderStatusCode,
        ...getOrderLineItems(order).map((li) => li.itemName ?? li.Item_Name),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [orders, search, isHistoryView, orderTypeFilter, hidePosActiveStatusFilter, activeStatusSelections, historyStatusSelections, historyFromDate, historyToDate]);

  const openDetail = (order) => {
    const id = order?.groceryOrderId ?? order?.orderId;
    if (!id) return;
    navigate(`/store-order/${id}`, {
      state: {
        fromOrderType: orderTypeFilter,
        fromOrderView: orderView,
      },
    });
  };

  const handleStatusUpdate = async (order, orderStatusCode, { confirmLabel } = {}) => {
    if (!allowOrderStatusUpdate) return;
    const id = order?.groceryOrderId ?? order?.orderId;
    if (!id || !updatedById) return;
    const label = confirmLabel || 'Update';
    const ok = await confirmAction({
      title: `${label} order?`,
      message: `${label} order #${order.displayOrderRef || id}?`,
      confirmLabel: label,
    });
    if (!ok) return;

    setActionId(id);
    try {
      const norm = String(orderStatusCode ?? '').toUpperCase();
      const isComplete =
        norm.includes('CMP') ||
        norm.includes('COMPLETE') ||
        norm.includes('DELIVER') ||
        norm === 'DEL';
      await updateGroceryOrderStatus(id, {
        orderStatusCode,
        updatedBy: updatedById,
        ...(isComplete ? { deliveredDate: new Date().toISOString() } : {}),
      });
      await loadOrders();
    } catch (e) {
      window.alert(e?.response?.data?.message || 'Failed to update order status.');
    } finally {
      setActionId(null);
    }
  };

  const handleAdvanceOrder = async (order, nextStep) => {
    if (!nextStep) return;
    if (
      nextStep.requiresDeliveryAgent &&
      isDeliveryStoreOrder(order) &&
      !order.deliveryUserId
    ) {
      handleOpenAssignModal(order);
      return;
    }
    if (isPosChannel && nextStep.nextKey === 'COMPLETED') {
      const id = order?.groceryOrderId ?? order?.orderId;
      if (!id) return;
      navigate('/home?tab=pos', { state: { completeOrderId: id } });
      return;
    }
    await handleStatusUpdate(order, nextStep.nextStatus, {
      confirmLabel: nextStep.actionLabel,
    });
  };

  const handleCancelOrder = async (order) => {
    await handleStatusUpdate(order, 'CANCELLED', { confirmLabel: 'Cancel' });
  };

  const closeAssignModal = () => {
    setAssignModalOpen(false);
    setAssignOrder(null);
    setSelectedAgentId('');
    setDeliveryAgents([]);
    setAssignSaving(false);
    setAgentsLoading(false);
    setExpectedDeliveryDate('');
    setExpectedDeliveryTime('');
  };

  const loadAgentsForOrder = async (order) => {
    setAgentsLoading(true);
    try {
      // Always use the fulfilling store's delivery agents (active store), not the buyer restaurant.
      const fulfillmentStoreId = storeId || order?.storeId || order?.supermarketId;
      if (fulfillmentStoreId) {
        return await fetchStoreDeliveryAgents(fulfillmentStoreId);
      }
      return [];
    } finally {
      setAgentsLoading(false);
    }
  };

  const handleOpenAssignModal = async (order) => {
    setAssignOrder(order);
    setSelectedAgentId(
      order?.deliveryUserId != null ? String(order.deliveryUserId) : ''
    );
    const initial = getDisplayableExpectedDeliveryTime(order);
    const { date, time } = clampExpectedDeliveryParts(
      initial ? toDateInputValue(initial) : expectedDeliveryMinDateValue(order?.createdDate),
      initial ? toTimeInputValue(initial) : '',
      order?.createdDate
    );
    setExpectedDeliveryDate(date);
    setExpectedDeliveryTime(time);
    setAssignModalOpen(true);
    const agents = await loadAgentsForOrder(order);
    setDeliveryAgents(agents);
    if (order?.deliveryUserId != null) {
      const match = agents.find(
        (a) => getDeliveryAgentRecordId(a) === String(order.deliveryUserId)
      );
      if (match) setSelectedAgentId(getDeliveryAgentRecordId(match));
    }
  };

  const handleExpectedDeliveryDateChange = (date) => {
    const { date: nextDate, time: nextTime } = clampExpectedDeliveryParts(
      date,
      expectedDeliveryTime,
      assignOrder?.createdDate
    );
    setExpectedDeliveryDate(nextDate);
    setExpectedDeliveryTime(nextTime);
  };

  const handleExpectedDeliveryTimeChange = (time) => {
    const { date: nextDate, time: nextTime } = clampExpectedDeliveryParts(
      expectedDeliveryDate,
      time,
      assignOrder?.createdDate
    );
    setExpectedDeliveryDate(nextDate);
    setExpectedDeliveryTime(nextTime);
  };

  const handleAssignDeliveryAgent = async () => {
    const order = assignOrder;
    const id = order?.groceryOrderId ?? order?.orderId;
    if (!id || !updatedById || !selectedAgentId) {
      window.alert('Please select a delivery agent.');
      return;
    }
    const agent = deliveryAgents.find(
      (a) => getDeliveryAgentRecordId(a) === selectedAgentId
    );
    if (!agent) return;

    const expectedDeliveryTimeApi = formatExpectedDeliveryTimeForApi(
      joinDateAndTimeLocal(expectedDeliveryDate, expectedDeliveryTime)
    );
    if (!expectedDeliveryTimeApi) {
      window.alert('Please enter expected delivery date and time.');
      return;
    }
    if (isExpectedDeliveryBeforeCreated(expectedDeliveryTimeApi, order.createdDate)) {
      window.alert('Expected delivery date and time cannot be earlier than the order created time.');
      return;
    }

    setAssignSaving(true);
    try {
      const statusNorm = normalizeStoreOrderStatusNorm(order);
      const shouldSetOfd = ['NEW', 'PENDING', 'READY', 'RDY'].includes(statusNorm);
      const orderStatusCode =
        order.orderStatusCode ||
        order.orderStatus ||
        (shouldSetOfd ? 'OFD' : 'NEW');
      await updateGroceryOrderStatus(id, {
        orderStatusCode: shouldSetOfd ? 'OFD' : orderStatusCode,
        deliveryUserId: Number(getDeliveryAgentRecordId(agent)),
        deliveryUserName: getDeliveryAgentDisplayName(agent),
        deliveryUserPhone: getDeliveryAgentPhone(agent) || undefined,
        expectedDeliveryTime: expectedDeliveryTimeApi,
        updatedBy: updatedById,
      });
      closeAssignModal();
      await loadOrders();
    } catch (e) {
      window.alert(e?.response?.data?.message || 'Failed to assign delivery agent.');
    } finally {
      setAssignSaving(false);
    }
  };

  const assignAgentSelectOptions = useMemo(
    () => deliveryAgentsToSelectOptions(deliveryAgents),
    [deliveryAgents]
  );

  const expectedDeliveryMinDate = useMemo(
    () => expectedDeliveryMinDateValue(assignOrder?.createdDate),
    [assignOrder?.createdDate]
  );

  const expectedDeliveryMinTime = useMemo(
    () => expectedDeliveryMinTimeValue(assignOrder?.createdDate, expectedDeliveryDate),
    [assignOrder?.createdDate, expectedDeliveryDate]
  );

  const tableColSpan =
    6 +
    (showDeliveryAgentColumn ? 2 : 0) +
    1 +
    (!isHistoryView ? 1 : 0);

  return (
    <div className="menu orders-page store-orders-page">
      <div className="today-order-toolbar today-order-toolbar--dine-in store-orders-toolbar">
        <div className="today-order-toolbar__row">
          <div className="today-order-toolbar__main">
            {serviceChannelSelect}
            <CollapsibleFilters>
            <ToolbarRichSelect
              panelId="store-order-view-filter"
              value={normalizeStoreOrderView(orderView)}
              onChange={(v) => setOrderView(normalizeStoreOrderView(v))}
              options={ORDER_VIEW_OPTIONS}
              ariaLabel="Active orders or order history"
              panelTitle="Order view"
            />
            {!hidePosActiveStatusFilter ? (
              <OrderStatusFilterDropdown
                panelId={
                  isHistoryView ? 'store-history-status-filter' : 'store-active-status-filter'
                }
                statusOptions={statusFilterOpts}
                statusSelections={statusSelections}
                onToggleStatus={toggleStatusFilter}
              />
            ) : null}
            {isHistoryView ? (
              <OrderHistoryDateFilters
                fromDate={historyFromDate}
                toDate={historyToDate}
                onFromDateChange={setHistoryFromDate}
                onToDateChange={setHistoryToDate}
              />
            ) : null}
            </CollapsibleFilters>
            <button
              type="button"
              className="menu-toolbar-btn today-order-toolbar__refresh"
              onClick={loadOrders}
              disabled={loading}
              aria-label="Refresh orders"
              title="Refresh"
            >
              <FaSyncAlt className={loading ? 'is-spinning' : ''} aria-hidden />
            </button>
          </div>
        </div>
      </div>

      {error ? <p className="inventory-status">{error}</p> : null}
      {loading ? <p className="inventory-items-hint">Loading orders…</p> : null}

      {!loading && !error ? (
        <OrdersTablePanel
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search table…"
          searchAriaLabel="Search store orders"
          resultCount={filtered.length}
          className="today-order-table-container--dine-in store-orders-table-panel"
          tableWrapperClassName="order-history-table-container store-orders-table-wrapper"
        >
          <table className="menu-table">
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Items</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Type</th>
                  <th>{partyColumnLabel}</th>
                  {showDeliveryAgentColumn ? <th>Delivery agent</th> : null}
                  {showDeliveryAgentColumn ? <th>Expected delivery</th> : null}
                  <th>Created On</th>
                  {!isHistoryView ? <th>Actions</th> : null}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td
                      colSpan={tableColSpan}
                      className="inventory-items-hint inventory-table-empty-cell"
                    >
                      No {isHistoryView ? 'order history' : 'orders'} found.
                    </td>
                  </tr>
                ) : (
                  filtered.map((order) => {
                    const id = order.groceryOrderId ?? order.orderId;
                    const orderLabel = order.displayOrderRef || id;
                    const busy = actionId === id;
                    const lineItems = getOrderLineItems(order);
                    const nextStep = allowOrderStatusUpdate
                      ? getNextStoreOrderStep(orderTypeFilter, order)
                      : null;
                    const statusChannel = storeOrderStatusChannel(orderTypeFilter, order);
                    return (
                      <tr key={id}>
                        <td>
                          <button
                            type="button"
                            className="inventory-item-name-link"
                            onClick={() => openDetail(order)}
                          >
                            {orderLabel}
                          </button>
                        </td>
                        <td className="store-orders-items-cell">
                          <OrderItemsSummary
                            items={lineItems}
                            onClick={() => openDetail(order)}
                            emptyText="—"
                          />
                        </td>
                        <td className="purchase-amount-cell">
                          {formatMoney(order.grandTotal ?? order.totalPrice)}
                        </td>
                        <td>
                          <OrderStatusPill
                            status={order.orderStatusCode || order.orderStatus}
                            channel={statusChannel}
                          />
                        </td>
                        <td>{order.orderType || '—'}</td>
                        <td>
                          {isRestaurantChannel
                            ? formatStoreOrderRestaurant(order)
                            : formatStoreOrderCustomer(order)}
                        </td>
                        {showDeliveryAgentColumn ? (
                          <td>
                            {!isDeliveryStoreOrder(order) ? (
                              '—'
                            ) : order.deliveryUserName ? (
                              <button
                                type="button"
                                className="assign-agent-link assigned-agent-name"
                                disabled={busy}
                                onClick={() => handleOpenAssignModal(order)}
                              >
                                {order.deliveryUserName}
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="assign-agent-link"
                                disabled={busy}
                                onClick={() => handleOpenAssignModal(order)}
                              >
                                Assign
                              </button>
                            )}
                          </td>
                        ) : null}
                        {showDeliveryAgentColumn ? (
                          <td className="store-orders-expected-delivery-cell">
                            {isDeliveryStoreOrder(order)
                              ? formatDisplayableExpectedDelivery(order)
                              : '—'}
                          </td>
                        ) : null}
                        <td>{formatDate(order.createdDate)}</td>
                        {!isHistoryView ? (
                          <td className="menu-table-actions-cell">
                            <div className="store-order-row-actions">
                              {nextStep ? (
                                <button
                                  type="button"
                                  className="order-next-step-btn"
                                  disabled={busy}
                                  title={nextStep.actionLabel}
                                  onClick={() => handleAdvanceOrder(order, nextStep)}
                                >
                                  {nextStep.actionLabel}
                                </button>
                              ) : null}
                              {allowOrderStatusUpdate ? (
                              <button
                                type="button"
                                className="purchase-btn purchase-btn--secondary store-order-cancel-btn"
                                disabled={busy}
                                onClick={() => handleCancelOrder(order)}
                              >
                                Cancel
                              </button>
                              ) : null}
                            </div>
                          </td>
                        ) : null}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </OrdersTablePanel>
      ) : null}

      {assignModalOpen ? (
        <div className="popup-overlay">
          <div className="popup-box popup-box--update-order">
            <div className="assign-agents-modal-header">
              <h2>Assign delivery agent</h2>
              <button
                type="button"
                className="menu-template-modal-close"
                aria-label="Close"
                onClick={closeAssignModal}
                disabled={assignSaving}
              >
                <FaTimes />
              </button>
            </div>
            <p className="today-order-modal-head-secondary">
              Order #{assignOrder?.displayOrderRef || assignOrder?.groceryOrderId}
            </p>
            <div className="assign-agents-form">
            {agentsLoading ? (
              <p className="inventory-items-hint">Loading delivery agents…</p>
            ) : deliveryAgents.length === 0 ? (
              <p className="today-order-modal-no-agents">No delivery agents available.</p>
            ) : (
              <SearchableSelect
                id="store-order-delivery-agent"
                value={selectedAgentId}
                onChange={setSelectedAgentId}
                options={assignAgentSelectOptions}
                placeholder="— Select delivery agent —"
                searchPlaceholder="Search delivery agents"
                disabled={assignSaving}
                className="assign-agents-form__field"
                triggerClassName="assign-agents-form__trigger"
                dropdownMinWidth={280}
                emptyMessage="No agents found"
              />
            )}
              <label
                className="today-order-modal-field-label assign-agents-form__agent-label"
              >
                Expected delivery date and time
              </label>
              <div className="assign-agents-form__datetime-row">
                <input
                  id="store-order-expected-delivery-date"
                  type="date"
                  className="assign-agents-form__datetime assign-agents-form__datetime--date"
                  value={expectedDeliveryDate}
                  onChange={(e) => handleExpectedDeliveryDateChange(e.target.value)}
                  min={expectedDeliveryMinDate || undefined}
                  disabled={assignSaving || agentsLoading}
                  aria-label="Expected delivery date"
                />
                <StoreExpectedDeliveryTimePicker
                  id="store-order-expected-delivery-time"
                  value={expectedDeliveryTime}
                  minTime={expectedDeliveryMinTime}
                  onChange={handleExpectedDeliveryTimeChange}
                  disabled={assignSaving || agentsLoading}
                  aria-label="Expected delivery time"
                />
              </div>
            <div className="assign-agents-form__actions">
              <button
                type="button"
                className="add-button"
                onClick={handleAssignDeliveryAgent}
                disabled={assignSaving || agentsLoading || deliveryAgents.length === 0}
              >
                {assignSaving ? 'Assigning…' : 'Assign'}
              </button>
            </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default StoreOrders;
